/**
 * @fileoverview Full-screen, non-dismissible gate shown when the backend
 * reports this app version is below the supported floor (updateRequired). The
 * only action is to open the store listing. Rendered in place of the app's
 * navigation, so a too-old client can't reach any screen.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius, type ColorTheme } from '../utils/theme';
import type { VersionCheck } from '../api/versionGate';

export default function UpdateRequiredScreen({ info }: { info: VersionCheck }) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const openStore = () => {
    if (info.storeUrl) Linking.openURL(info.storeUrl).catch(() => { /* store app unavailable */ });
  };

  return (
    <View style={styles.container}>
      <View style={styles.iconWrap}>
        <Ionicons name="cloud-download-outline" size={64} color={colors.primary} />
      </View>
      <Text style={styles.title}>Update RinsePay</Text>
      <Text style={styles.body}>
        {info.message || 'This version of RinsePay is no longer supported. Please update to the latest version to keep washing.'}
      </Text>
      {info.storeUrl ? (
        <TouchableOpacity style={styles.button} onPress={openStore} accessibilityRole="button">
          <Ionicons name="arrow-up-circle" size={20} color={colors.white} />
          <Text style={styles.buttonText}>Update Now</Text>
        </TouchableOpacity>
      ) : null}
      <Text style={styles.slogan}>PAYMENTS MADE SIMPLE</Text>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  iconWrap: {
    width: 112, height: 112, borderRadius: 56, alignItems: 'center', justifyContent: 'center',
    backgroundColor: c.primaryBg, marginBottom: spacing.lg,
  },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold as any, color: c.text, marginBottom: spacing.sm, textAlign: 'center' },
  body: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: spacing.xl, maxWidth: 340 },
  button: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: c.primary, paddingHorizontal: spacing.xl, paddingVertical: spacing.md, borderRadius: borderRadius.md,
  },
  buttonText: { color: c.white, fontSize: fontSize.md, fontWeight: fontWeight.semibold as any },
  slogan: { position: 'absolute', bottom: spacing.xl, color: c.primary, fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any, letterSpacing: 1 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
